import type { MeetingRecord } from '../../types/meetings'
import type { HuddleSession } from '../../services/huddleSessionService'

export type MeetingScheduleEntry = {
  kind: 'meeting'
  id: string
  title: string
  startsAt: Date
  endsAt: Date
  meeting: MeetingRecord
} | {
  kind: 'huddle'
  id: string
  title: string
  startsAt: Date
  endsAt: Date
  huddle: HuddleSession
}

export type WeekScheduleEntry = {
  entry: MeetingScheduleEntry
  startMin: number
  endMin: number
}

type DateValue = { toDate: () => Date } | Date | string | null | undefined

const DEFAULT_ONGOING_HUDDLE_DURATION_MS = 30 * 60_000

export const scheduleEntryDate = (value: DateValue) => {
  if (value instanceof Date) return value
  if (value && typeof value === 'object' && 'toDate' in value) return value.toDate()
  return value ? new Date(value) : null
}

export const buildMeetingScheduleEntries = (
  meetings: MeetingRecord[],
  huddleSessions: HuddleSession[],
  includeHuddles: boolean,
): MeetingScheduleEntry[] => {
  const meetingEntries: MeetingScheduleEntry[] = meetings.map((meeting) => {
    const startsAt = scheduleEntryDate(meeting.scheduledAt) ?? new Date(0)
    return {
      kind: 'meeting',
      id: meeting.id,
      title: meeting.title,
      startsAt,
      endsAt: new Date(startsAt.getTime() + (meeting.durationMinutes ?? 30) * 60_000),
      meeting,
    }
  })
  if (!includeHuddles) return meetingEntries

  const huddleEntries = huddleSessions.flatMap((huddle): MeetingScheduleEntry[] => {
    const startsAt = scheduleEntryDate(huddle.startedAt)
    if (!startsAt) return []
    const endedAt = scheduleEntryDate(huddle.endedAt)
    return [{
      kind: 'huddle',
      id: `huddle:${huddle.id}`,
      title: huddle.channelName ? `Huddle: ${huddle.channelName}` : 'Huddle',
      startsAt,
      endsAt: endedAt && endedAt > startsAt ? endedAt : new Date(startsAt.getTime() + DEFAULT_ONGOING_HUDDLE_DURATION_MS),
      huddle,
    }]
  })
  return [...meetingEntries, ...huddleEntries]
}

export const scheduleDayKey = (date: Date) => `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`

export const bucketScheduleEntriesByDay = (entries: MeetingScheduleEntry[]) => {
  const buckets = new Map<string, MeetingScheduleEntry[]>()
  for (const entry of entries) {
    const key = scheduleDayKey(entry.startsAt)
    const bucket = buckets.get(key)
    if (bucket) bucket.push(entry)
    else buckets.set(key, [entry])
  }
  for (const bucket of buckets.values()) {
    bucket.sort((a, b) => a.startsAt.getTime() - b.startsAt.getTime() || a.id.localeCompare(b.id))
  }
  return buckets
}

export const layoutScheduleEntriesForWeek = (
  entries: MeetingScheduleEntry[],
  weekStart: Date,
  weekEnd: Date,
) => {
  const buckets = new Map<number, WeekScheduleEntry[]>()
  for (let dayIdx = 0; dayIdx < 7; dayIdx++) buckets.set(dayIdx, [])
  const rangeStartMs = weekStart.getTime()
  const rangeEndMs = weekEnd.getTime()
  for (const entry of entries) {
    const begins = entry.startsAt.getTime()
    const finishes = entry.endsAt.getTime()
    if (finishes <= rangeStartMs || begins >= rangeEndMs) continue
    for (let dayIdx = 0; dayIdx < 7; dayIdx++) {
      const dayStart = rangeStartMs + dayIdx * 24 * 60 * 60 * 1000
      const dayEnd = dayStart + 24 * 60 * 60 * 1000
      if (finishes <= dayStart || begins >= dayEnd) continue
      const startMin = Math.max(0, (Math.max(begins, dayStart) - dayStart) / 60_000)
      const endMin = Math.min(24 * 60, (Math.min(finishes, dayEnd) - dayStart) / 60_000)
      if (endMin - startMin >= 5) buckets.get(dayIdx)!.push({ entry, startMin, endMin })
    }
  }
  for (const bucket of buckets.values()) {
    bucket.sort((a, b) => a.startMin - b.startMin || a.entry.id.localeCompare(b.entry.id))
  }
  return buckets
}

export const calendarSyncBadge = (status: MeetingRecord['gcalStatus']) => {
  if (status === 'SYNCED') return { tone: 'success' as const, label: 'Calendar synced' }
  if (status === 'FAILED') return { tone: 'critical' as const, label: 'Calendar needs attention' }
  return null
}
